import React, { Suspense } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { AudioFormat } from '../../../api/cc-smarts/getAudio';
import type { AudioFeature } from '../../../types/audioFeature';
import { AudioPlayerStreamingProvider } from '../../context/AudioPlayerStreamingProvider';
import { useAudioPlaybackSync } from '../../hooks/useAudioPlaybackSync';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreaming } from '../../hooks/useAudioPlayerStreaming';
import { useHoverWithDelayedExit } from '../../hooks/useHoverWithDelayedExit';

import { AutoPlayManager } from './AutoPlayManager';
import { ExpandedAudioPlayer } from './ExpandedAudioPlayer';
import { MinifiedAudioPlayer } from './MinifiedAudioPlayer';

const styles = cssMap({
	boxShadowWrapper: {
		boxShadow: token('elevation.shadow.overflow'),
		borderRadius: token('radius.xlarge'),
	},
	container: {
		borderRadius: token('radius.xlarge'),
		position: 'relative',
		display: 'inline-block',
		verticalAlign: 'top',
		width: 'fit-content',
		overflow: 'hidden',
	},
});

type AudioContainerProps = {
	product: string;
	cloudId: string;
	activationId: string;
	audioFeatures: AudioFeature[];
};

export const AudioContainer = ({
	product,
	cloudId,
	activationId,
	audioFeatures,
}: AudioContainerProps) => {
	const { activeFeature } = useAudioPlayerStore();
	const { isHovered, handleMouseEnter, handleMouseLeave } = useHoverWithDelayedExit(3000, true);
	const { activeContent } = useAudioPlayerStore();

	const { audioElementRef, fetchAudio } = useAudioPlayerStreaming({
		locale: 'en-US',
		type: activeFeature,
		format: AudioFormat.MP3,
		product,
		cloudId,
		activationId,
	});

	useAudioPlaybackSync({ audioRef: audioElementRef });

	return (
		<AudioPlayerStreamingProvider fetchAudio={fetchAudio}>
			{/* Enable auto-play of next track when current track ends */}
			<Suspense fallback={null}>
				<AutoPlayManager cloudId={cloudId} audioElementRef={audioElementRef} />
			</Suspense>

			<Box xcss={styles.boxShadowWrapper}>
				<Box
					xcss={styles.container}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
				>
					{isHovered || true ? (
						<ExpandedAudioPlayer audioRef={audioElementRef} audioFeatures={audioFeatures} />
					) : (
						<MinifiedAudioPlayer
							audioRef={audioElementRef}
							title={activeContent.title || 'Untitled'}
							byline={activeContent.author || ''}
							avatarUrl={activeContent.avatarUrl || ''}
						/>
					)}
					<audio ref={audioElementRef} />
				</Box>
			</Box>
		</AudioPlayerStreamingProvider>
	);
};
